import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Phone, CheckCircle, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","District of Columbia","Florida","Georgia","Hawaii","Idaho","Illinois",
  "Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts",
  "Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada",
  "New Hampshire","New Jersey","New Mexico","New York","North Carolina","North Dakota",
  "Ohio","Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming","Outside USA"
];
const PRODUCTS = ["Land Plot", "Apartment Unit", "Hotel Share", "Not Sure"];

// Accept any international phone number: 7–15 digits (E.164), optional leading +
function isValidPhone(phone) {
  const digits = phone.replace(/[^\d]/g, '');
  return digits.length >= 7 && digits.length <= 15;
}

const DEFAULT_FORM_FIELDS = [
  { field_id: 'first_name', label: 'First Name', field_type: 'text', required: true, visible: true, order: 0, options: [], placeholder: 'Enter first name' },
  { field_id: 'last_name', label: 'Last Name', field_type: 'text', required: true, visible: true, order: 1, options: [], placeholder: 'Enter last name' },
  { field_id: 'phone', label: 'Phone Number', field_type: 'text', required: true, visible: true, order: 2, options: [], placeholder: '+1 555 123 4567' },
  { field_id: 'email', label: 'Email', field_type: 'text', required: false, visible: true, order: 3, options: [], placeholder: 'john@example.com' },
  { field_id: 'state', label: 'State', field_type: 'dropdown', required: false, visible: true, order: 4, options: [], placeholder: 'Select state' },
  { field_id: 'interested_product', label: 'Interested Product', field_type: 'dropdown', required: false, visible: true, order: 5, options: [], placeholder: 'Select product' },
  { field_id: 'notes', label: 'Notes / Comments', field_type: 'text', required: false, visible: true, order: 6, options: [], placeholder: 'Any questions or comments...' },
  { field_id: 'consent', label: 'I agree to be contacted regarding my inquiry', field_type: 'checkbox', required: true, visible: true, order: 7, options: [] },
];

const FONT_LINKS = {
  'Inter': 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap',
  'Poppins': 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap',
  'Roboto': 'https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap',
  'Open Sans': 'https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;500;600;700&display=swap',
  'Lato': 'https://fonts.googleapis.com/css2?family=Lato:wght@400;700&display=swap',
  'Montserrat': 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700&display=swap'
};

const getYouTubeId = (url) => {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
  return match ? match[1] : null;
};

const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || undefined,
    utm_medium: params.get('utm_medium') || undefined,
    utm_campaign: params.get('utm_campaign') || undefined,
    utm_content: params.get('utm_content') || undefined,
    referrer: document.referrer || undefined
  };
};

function getDropdownOptions(fieldId, options) {
  if (options && options.length > 0) return options;
  if (fieldId === 'state') return US_STATES;
  if (fieldId === 'interested_product') return PRODUCTS;
  return [];
}

function getBtnPadding(size) {
  if (size === 'sm') return '8px 20px';
  if (size === 'lg') return '18px 44px';
  return '12px 28px';
}

// Social Icons
const SocialIcon = ({ type, url }) => {
  if (!url) return null;
  const icons = {
    facebook: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
    instagram: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
    youtube: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>,
    linkedin: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
    whatsapp: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
  };
  return <a href={url} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-slate-900 transition-colors">{icons[type]}</a>;
};

const TrustHeader = ({ globalSettings }) => {
  if (!globalSettings) return null;
  const { logo_url, phone_number, facebook_url, instagram_url, youtube_url, linkedin_url, whatsapp_url } = globalSettings;
  const hasSocialLinks = facebook_url || instagram_url || youtube_url || linkedin_url || whatsapp_url;
  if (!logo_url && !phone_number && !hasSocialLinks) return null;
  return (
    <header className="bg-white border-b border-slate-200 h-[60px] md:h-[70px] fixed top-0 left-0 right-0 z-50">
      <div className="max-w-7xl mx-auto px-4 h-full flex items-center justify-between">
        <div className="flex-shrink-0">
          {logo_url ? <img src={`${API_URL}${logo_url}`} alt="Logo" className="h-10 md:h-12 object-contain" /> : <div className="h-10 md:h-12" />}
        </div>
        <div className="flex items-center gap-4 md:gap-6">
          {phone_number && (
            <a href={`tel:${phone_number.replace(/\s+/g, '')}`} className="flex items-center gap-1.5 text-slate-700 hover:text-slate-900 font-medium text-sm md:text-base">
              <Phone size={18} className="text-blue-600" /><span className="hidden sm:inline">{phone_number}</span>
            </a>
          )}
          {hasSocialLinks && (
            <div className="flex items-center gap-3">
              <SocialIcon type="facebook" url={facebook_url} /><SocialIcon type="instagram" url={instagram_url} />
              <SocialIcon type="youtube" url={youtube_url} /><SocialIcon type="linkedin" url={linkedin_url} />
              <SocialIcon type="whatsapp" url={whatsapp_url} />
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

// Reusable CTA Buttons Component
const CTAButtons = ({ page, onOpenForm, className = '', variant = 'default' }) => {
  const layout = page.button_layout || 'side_by_side';
  const primaryColor = page.button_color || '#2563EB';
  const primaryTextColor = page.button_text_color || '#FFFFFF';
  const secBgColor = page.btn_secondary_bg_color;
  const secTextColor = page.btn_secondary_text_color || primaryColor;
  const primaryFont = page.btn_primary_font_family || page.font_family || 'Inter';
  const secondaryFont = page.btn_secondary_font_family || page.font_family || 'Inter';

  const showPrimary = layout !== 'secondary_only';
  const showSecondary = layout !== 'primary_only';
  const isStacked = layout === 'stacked';

  // For inverted CTA (on colored background)
  const isInverted = variant === 'inverted';

  return (
    <div className={`flex gap-4 ${isStacked ? 'flex-col items-center' : 'flex-col sm:flex-row'} ${className}`}>
      {showPrimary && (
        <Button
          onClick={onOpenForm}
          className="flex-1 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all"
          style={{
            backgroundColor: isInverted ? primaryTextColor : primaryColor,
            color: isInverted ? primaryColor : primaryTextColor,
            fontFamily: primaryFont,
            fontSize: `${page.btn_primary_font_size || '16'}px`,
            padding: getBtnPadding(page.btn_primary_size || 'md'),
            height: 'auto', minHeight: page.btn_primary_size === 'sm' ? '40px' : page.btn_primary_size === 'lg' ? '56px' : '48px'
          }}
          data-testid="cta-primary"
        >
          {page.button_text_primary || 'Check Eligibility'} <ArrowRight className="ml-2" size={18} />
        </Button>
      )}
      {showSecondary && (
        <Button
          onClick={onOpenForm}
          variant="outline"
          className="flex-1 rounded-xl font-semibold transition-all"
          style={{
            backgroundColor: isInverted ? 'transparent' : (secBgColor || 'transparent'),
            borderColor: isInverted ? primaryTextColor : (secBgColor ? 'transparent' : primaryColor),
            color: isInverted ? primaryTextColor : secTextColor,
            fontFamily: secondaryFont,
            fontSize: `${page.btn_secondary_font_size || '16'}px`,
            padding: getBtnPadding(page.btn_secondary_size || 'md'),
            height: 'auto', minHeight: page.btn_secondary_size === 'sm' ? '40px' : page.btn_secondary_size === 'lg' ? '56px' : '48px',
            borderWidth: '2px'
          }}
          data-testid="cta-secondary"
        >
          <Phone className="mr-2" size={18} /> {page.button_text_secondary || 'Contact Us'}
        </Button>
      )}
    </div>
  );
};

// Dynamic Form Field Renderer
const FormField = ({ field, value, onChange }) => {
  const opts = getDropdownOptions(field.field_id, field.options);

  if (field.field_type === 'checkbox') {
    return (
      <div className="flex items-start gap-2 pt-2">
        <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} className="mt-1 rounded" />
        <Label className="text-sm text-slate-600">{field.label} {field.required && '*'}</Label>
      </div>
    );
  }

  if (field.field_type === 'dropdown') {
    return (
      <div>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger className="mt-1"><SelectValue placeholder={field.placeholder || `Select ${field.label}`} /></SelectTrigger>
          <SelectContent className="max-h-60">
            {opts.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.field_type === 'date') {
    return (
      <div>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className="mt-1" />
      </div>
    );
  }

  // Default: text (also handles multiline for 'notes')
  if (field.field_id === 'notes' || field.field_id.includes('notes') || field.field_id.includes('comment')) {
    return (
      <div>
        <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
        <Textarea value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} rows={2} className="mt-1" />
      </div>
    );
  }

  return (
    <div>
      <Label>{field.label} {field.required && <span className="text-red-500">*</span>}</Label>
      <Input
        type={field.field_id === 'email' ? 'email' : field.field_id === 'phone' ? 'tel' : 'text'}
        value={value || ''} onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder} className="mt-1"
      />
    </div>
  );
};

export default function PublicLandingPage() {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const pageResponse = await fetch(`${API_URL}/api/public/lp/${slug}`);
        if (pageResponse.ok) {
          const pageData = await pageResponse.json();
          setPage(pageData);
          if (pageData.font_family && FONT_LINKS[pageData.font_family]) {
            const link = document.createElement('link');
            link.href = FONT_LINKS[pageData.font_family];
            link.rel = 'stylesheet';
            document.head.appendChild(link);
          }
        } else { setNotFound(true); }

        const settingsResponse = await fetch(`${API_URL}/api/public/landing-pages/global-settings`);
        if (settingsResponse.ok) setGlobalSettings(await settingsResponse.json());

        const eventsResponse = await fetch(`${API_URL}/api/public/upcoming-events`);
        if (eventsResponse.ok) setEvents(await eventsResponse.json());
      } catch (error) {
        logger.error('Error fetching data:', error);
        setNotFound(true);
      } finally { setLoading(false); }
    };
    fetchData();
  }, [slug]);

  const formFields = useMemo(() => {
    const fields = (page?.form_fields && page.form_fields.length > 0) ? page.form_fields : DEFAULT_FORM_FIELDS;
    return [...fields].filter(f => f.visible).sort((a, b) => a.order - b.order);
  }, [page]);

  const openFormModal = () => {
    setFormValues({});
    setSubmitted(false);
    setShowFormModal(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Validate required fields
    for (const field of formFields) {
      if (!field.required) continue;
      const val = formValues[field.field_id];
      if (!val || (typeof val === 'string' && !val.trim()) || (typeof val === 'boolean' && !val)) {
        toast.error(`${field.label} is required`);
        return;
      }
    }

    setSubmitting(true);
    try {
      // Validate US phone format
      const phoneVal = formValues['phone'] || '';
      if (phoneVal) {
        if (!isValidPhone(phoneVal)) {
          toast.error('Please enter a valid phone number including your country code (e.g. +1 555 123 4567 or +880 1712 345678).');
          setSubmitting(false);
          return;
        }
      }

      const response = await fetch(`${API_URL}/api/public/lp/${slug}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formValues, ...getUTMParams(), event_id: formValues.event_id })
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitted(true);
        setTimeout(() => {
          setShowFormModal(false);
          setSubmitted(false);
          setFormValues({});
        }, 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    } finally { setSubmitting(false); }
  };

  const handleTileClick = (tile) => {
    if (tile.click_action === 'url' && tile.click_url) {
      window.open(tile.click_url, '_blank');
    } else {
      openFormModal();
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }
  if (notFound) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 p-4">
        <h1 className="text-4xl font-bold text-slate-900 mb-4">Page Not Found</h1>
        <p className="text-slate-600 mb-8">The page you're looking for doesn't exist or is no longer available.</p>
        <Link to="/login"><Button variant="outline">Go to Login</Button></Link>
      </div>
    );
  }

  const youtubeId = getYouTubeId(page.youtube_url);
  const youtubeId2 = getYouTubeId(page.youtube_url_2);
  const buttonColor = page.button_color || '#2563EB';
  const buttonTextColor = page.button_text_color || '#FFFFFF';
  const fontFamily = page.font_family || 'Inter';
  const backgroundColor = page.background_color || '#FFFFFF';
  const gradientEnd = page.background_gradient_end;
  const gradientDirection = page.background_gradient_direction || 'to bottom';
  const bgOpacity = page.background_opacity ?? 1.0;
  const bgStyle = gradientEnd ? `linear-gradient(${gradientDirection}, ${backgroundColor}, ${gradientEnd})` : backgroundColor;
  const hasHeader = globalSettings && (globalSettings.logo_url || globalSettings.phone_number);
  const thankYouMessage = page.thank_you_message || 'Thank you for your interest! Our team will contact you shortly.';

  return (
    <div className="min-h-screen pb-20 md:pb-0" style={{ background: bgStyle, opacity: bgOpacity, fontFamily }} data-testid="public-landing-page">
      <TrustHeader globalSettings={globalSettings} />

      <main className={hasHeader ? 'pt-[60px] md:pt-[70px]' : ''}>
        {/* Hero */}
        <section className="relative">
          {page.hero_image_url && (
            <img src={`${API_URL}${page.hero_image_url}`} alt={page.title} className="w-full h-auto hidden md:block object-cover" style={{ maxHeight: '900px' }} />
          )}
          <img src={`${API_URL}${page.hero_mobile_image_url || page.hero_image_url}`} alt={page.title} className="w-full h-auto md:hidden object-cover" />
        </section>

        {/* CTA Buttons */}
        <section className="py-8 px-4" style={{ background: gradientEnd ? `linear-gradient(${gradientDirection}, ${backgroundColor}ee, ${gradientEnd})` : `linear-gradient(to bottom, ${backgroundColor}ee, ${backgroundColor})` }}>
          <div className="max-w-md mx-auto">
            <CTAButtons page={page} onOpenForm={openFormModal} />
          </div>
        </section>

        {/* YouTube Video 1 */}
        {youtubeId && (
          <section className="py-12 px-4 bg-slate-900">
            <div className="max-w-4xl mx-auto">
              <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl shadow-2xl">
                <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${youtubeId}?rel=0`} title="Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
            <div className="max-w-md mx-auto mt-8 hidden md:block">
              <CTAButtons page={page} onOpenForm={openFormModal} variant="inverted" />
            </div>
          </section>
        )}

        {/* Trust Strip */}
        {page.trust_images && page.trust_images.length > 0 && (
          <section className="py-10 px-4 bg-slate-50">
            <div className="max-w-5xl mx-auto">
              <div className={`grid gap-6 ${page.trust_images.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : page.trust_images.length === 2 ? 'grid-cols-2 max-w-2xl mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
                {page.trust_images.map((img, idx) => (
                  <div key={`trust-${img}`} className="bg-white p-4 rounded-xl shadow-sm">
                    <img src={`${API_URL}${img}`} alt={`Trust ${idx + 1}`} className="w-full h-auto object-contain" style={{ maxHeight: '150px' }} />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* YouTube Video 2 */}
        {youtubeId2 && (
          <section className="py-12 px-4" style={{ backgroundColor }}>
            <div className="max-w-4xl mx-auto">
              <div className="relative pb-[56.25%] h-0 overflow-hidden rounded-xl shadow-2xl">
                <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${youtubeId2}?rel=0`} title="Video 2" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
              </div>
            </div>
          </section>
        )}

        {/* Offer Tiles — Clickable */}
        {page.offer_tiles && page.offer_tiles.length > 0 && (
          <section className="py-12 px-4">
            <div className="max-w-5xl mx-auto">
              <div className={`grid gap-6 ${page.offer_tiles.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : page.offer_tiles.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-3'}`}>
                {page.offer_tiles.filter(t => t.image_url).map((tile, idx) => (
                  <div
                    key={tile.image_url}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all cursor-pointer group"
                    onClick={() => handleTileClick(tile)}
                    data-testid={`offer-tile-${idx}`}
                  >
                    <div className="overflow-hidden">
                      <img src={`${API_URL}${tile.image_url}`} alt={tile.caption || `Offer ${idx + 1}`} className="w-full h-auto object-cover group-hover:scale-[1.02] transition-transform duration-300" />
                    </div>
                    {tile.caption && (
                      <div className="p-4 flex items-center justify-between">
                        <p className="font-medium text-slate-900">{tile.caption}</p>
                        <ArrowRight size={16} className="text-slate-400 group-hover:text-slate-700 transition-colors" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Final CTA */}
        <section className="py-12 px-4" style={{ backgroundColor: buttonColor }}>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6" style={{ color: buttonTextColor }}>Ready to Get Started?</h2>
            <div className="hidden md:block max-w-md mx-auto">
              <CTAButtons page={page} onOpenForm={openFormModal} variant="inverted" />
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 bg-slate-900 text-center">
          {page.footer_text && <p className="text-slate-400 text-sm mb-4">{page.footer_text}</p>}
          {page.disclaimer_text && <p className="text-slate-500 text-xs max-w-2xl mx-auto">{page.disclaimer_text}</p>}
        </footer>
      </main>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 md:hidden z-50 shadow-lg">
        <CTAButtons page={page} onOpenForm={openFormModal} />
      </div>

      {/* Unified Lead Form Modal */}
      <Dialog open={showFormModal} onOpenChange={setShowFormModal}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" style={{ fontFamily }}>
          <DialogHeader>
            <DialogTitle className="text-xl">{page.button_text_primary || 'Check Eligibility'}</DialogTitle>
          </DialogHeader>

          {submitted ? (
            <div className="py-12 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900">Thank You!</h3>
              <p className="text-slate-600 mt-2">{thankYouMessage}</p>
            </div>
          ) : (
            <form onSubmit={handleFormSubmit} className="space-y-4 py-4" data-testid="lead-form">
              {formFields.map((field) => (
                <FormField
                  key={field.field_id}
                  field={field}
                  value={formValues[field.field_id]}
                  onChange={(val) => setFormValues(prev => ({ ...prev, [field.field_id]: val }))}
                />
              ))}

              {/* Event RSVP (if events exist) */}
              {events.length > 0 && (
                <div>
                  <Label>Register for Event (optional)</Label>
                  <Select value={formValues.event_id || ''} onValueChange={(v) => setFormValues(prev => ({ ...prev, event_id: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Select an event" /></SelectTrigger>
                    <SelectContent>
                      {events.map(event => (
                        <SelectItem key={event.event_id} value={event.event_id}>
                          {event.name} - {new Date(event.event_date).toLocaleDateString()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <Button
                type="submit"
                className="w-full h-12 rounded-xl font-semibold"
                style={{ backgroundColor: buttonColor, color: buttonTextColor }}
                disabled={submitting}
                data-testid="form-submit-btn"
              >
                {submitting ? <Loader2 className="animate-spin" /> : 'Submit'}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
